import React, { useMemo, useState, useCallback } from 'react';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import Label from 'dpl/form/components/Label';
import LocationAutocompleteContainer from 'dpl/search/containers/LocationAutocompleteContainer';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import ResponsiveSelect from 'dpl/components/ResponsiveSelect';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';

const IS_LT_MD = isLessThanBreakpoint('md');

export const OTHER_LOCATION_VALUE = 'other';

interface PickupLocationDropdownProps {
  value?: string;
  onChange: (value: string, attributes?: any) => void;
  breederProfileId: number | string;
  buyerProfileId?: number | string;
  userType?: string;
  disabled?: boolean;
  newDesignSystemStyles?: boolean;
}

export default function PickupLocationDropdown({
  value,
  onChange,
  breederProfileId,
  buyerProfileId,
  userType = 'breeder',
  disabled = false,
  newDesignSystemStyles = false
}: PickupLocationDropdownProps) {
  const [locationSearchValue, setLocationSearchValue] = useState('');

  const pickupLocationsQuery = useResourceQuery({
    url: `/api/options/breeder_profiles/${breederProfileId}/pickup_locations.json`,
    params: {
      user_type: userType,
      buyer_profile_id: buyerProfileId
    },
    enabled: Boolean(breederProfileId)
  });

  const options = useMemo(() => {
    if (!pickupLocationsQuery.isSuccess) {
      return [];
    }

    return [
      {
        label: 'Add a new location',
        value: OTHER_LOCATION_VALUE,
        section: 'custom',
        icon: 'fetch-plus',
        iconBgClassName: 'bg-blue-100'
      },
      ...pickupLocationsQuery.value.map((option: any) => ({
        ...option,
        icon:
          option.value === 'buyer_enters_location'
            ? 'fetch-edit'
            : 'fetch-location',
        iconBgClassName:
          option.section === 'custom' ? 'bg-blue-100' : 'bg-gray-200'
      }))
    ];
  }, [pickupLocationsQuery.value]);

  const handleDropdownChange = useCallback(
    (newValue: string) => {
      const selectedOption = options.find(option => option.value === newValue);

      setLocationSearchValue('');
      if (selectedOption) {
        onChange(newValue, (selectedOption as any).pickup_location_attributes);
      }
    },
    [onChange, options]
  );

  const handleLocationSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocationSearchValue(e.target.value);
    },
    [onChange, setLocationSearchValue]
  );

  const handleLocationSearchSelect = useCallback(
    async (location: any) => {
      setLocationSearchValue(location.name);
      const coordinates = await location.getCoordinates();
      if (coordinates) {
        onChange(OTHER_LOCATION_VALUE, {
          longitude: coordinates.lng,
          latitude: coordinates.lat,
          location_name: location.name
        });
      }
    },
    [onChange, setLocationSearchValue]
  );

  const [label, asteriskCopy] = useMemo(() => {
    if (userType === 'breeder') {
      return [
        'Dog pickup location*',
        <>
          *Any state and local sales tax is added to the buyer&apos;s total,
          based on pickup location.&nbsp;
          <TooltipWrapper
            minWidth="500px"
            buttonClassName="underline"
            portalClassName="z-max"
            title={
              <p>
                We calculate, collect, and pay the sales tax on this payment
                based on the pickup location, at no cost to you. Buyers see it
                as a separate line, so don&apos;t include tax in your price.
                <br />
                <br />
                <a
                  href="https://www.gooddog.com/good-breeder-center/sales-tax-on-good-dog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Learn more in our FAQs
                </a>
              </p>
            }
          >
            Learn more
          </TooltipWrapper>
        </>
      ];
    }

    return [
      'Where will you pick up your puppy?*',
      '*Used to calculate any required state & local taxes.'
    ];
  }, [userType]);

  return (
    <div className="PickupLocationDropdown">
      <ResponsiveSelect
        selectedValue={value}
        onChange={handleDropdownChange}
        // @ts-ignore
        options={options}
        id="pickup-location-select"
        placeholder="Select a location"
        label={label}
        disabled={disabled}
        newDesignSystemStyles={newDesignSystemStyles}
      />
      <p className="font-12 secondary mt2">{asteriskCopy}</p>
      {value === OTHER_LOCATION_VALUE && (
        <>
          <Label newDesignSystemStyles text="Location" className="mb2 mt6" />
          <LocationAutocompleteContainer
            omitStates
            menuCloseTimeout={IS_LT_MD ? 0 : 300}
            onSelect={handleLocationSearchSelect}
            query={locationSearchValue}
            classes={{ menu: 'flex-auto mt1' }}
            inputProps={{
              name: 'location-search',
              className: 'bg-white ba br3 b--default pv3 ph3 font-16 w-100',
              onChange: handleLocationSearchChange,
              placeholder: 'Enter city or ZIP code',
              disabled
            }}
            newDesignSystemStyles={newDesignSystemStyles}
          />
        </>
      )}
    </div>
  );
}
